import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Route } from '~/Constants/Routes.constants';
import { routeReplacer } from '~/Helpers/String.helper';
import { useNavigation, useProvider } from '~/Hooks';
import { IChecklistListItem } from '~/ExportedComponents/TPD/Checklist/checklistListItem.model';
import { useRequestChecklist } from '~/requests/providerService';
import { PartnerPortalChecklistStep } from '../../ExportedComponents/TPD/Checklist/types';
import { useLocations } from '~/store/provider';
import { Segment } from '~/Helpers/Segment.helper';
import { SegmentName } from '~/Constants/Segment.constants';

const {
  EVENTGROUPS,
  USER_SETTINGS,
  PROVIDER_LOCATIONS_EDIT,
  PROVIDER_LOCATIONS,
  PROVIDER_STAFF,
} = Route;

export const usePartnerPortalChecklist = () => {
  const { t } = useTranslation();
  const { navigate } = useNavigation();
  const {
    partnerPortalChecklist,
    updatePartnerPortalChecklist,
    isCheckListLoading,
    checklistError,
    dataUpdatedAt,
  } = useRequestChecklist();
  const { currentProvider } = useProvider();
  const { locationList } = useLocations();

  // CONSTANTS
  const domain = currentProvider?.domain || '';

  const locationLink = useMemo(
    () =>
      locationList?.[0]?.id
        ? routeReplacer(PROVIDER_LOCATIONS_EDIT, { domain, id: locationList[0].id })
        : routeReplacer(PROVIDER_LOCATIONS, { domain }),
    [locationList?.[0]?.id],
  );

  const partnerPortalActiveIndex = useMemo(() => {
    if (partnerPortalChecklist) {
      if (partnerPortalChecklist.studioDataEdited) {
        return PartnerPortalChecklistStep.DONE;
      }
      if (partnerPortalChecklist.studioDataEdited) {
        return PartnerPortalChecklistStep.STAFF_EDITED;
      }
      if (partnerPortalChecklist.eventEdited) {
        return PartnerPortalChecklistStep.STUDIO_DATA_EDITED;
      }
      if (partnerPortalChecklist.userProfileEdited) {
        return PartnerPortalChecklistStep.EVENT_EDITED;
      }
      return PartnerPortalChecklistStep.PROFILE_UPDATED;
    }
    return PartnerPortalChecklistStep.NO_STEPS;
  }, [partnerPortalChecklist]);

  const partnerPortalChecklistItems: IChecklistListItem[] = useMemo(
    () => [
      {
        index: 1,
        title: t('checklist.partnerPortalMigrated.item1.title'),
        description: t('checklist.partnerPortalMigrated.item1.description'),
        deactivated: !partnerPortalChecklist?.userProfileEdited,
        active: partnerPortalActiveIndex === PartnerPortalChecklistStep.PROFILE_UPDATED,
        onClick: () => {
          if (!partnerPortalChecklist?.userProfileEdited) {
            updatePartnerPortalChecklist({ userProfileEdited: new Date().toISOString() });
            Segment.track(SegmentName.PARTNER_PORTAL_CHECKLIST_PROFILE_UPDATED);
          }
          navigate(routeReplacer(USER_SETTINGS, { domain }));
        },
      },
      {
        index: 2,
        title: t('checklist.partnerPortalMigrated.item2.title'),
        description: t('checklist.partnerPortalMigrated.item2.description'),
        deactivated: !partnerPortalChecklist?.eventEdited,
        active: partnerPortalActiveIndex === PartnerPortalChecklistStep.EVENT_EDITED,
        onClick: () => {
          if (!partnerPortalChecklist?.eventEdited) {
            updatePartnerPortalChecklist({ eventEdited: new Date().toISOString() });
            Segment.track(SegmentName.PARTNER_PORTAL_CHECKLIST_EVENT_EDITED);
          }
          navigate(routeReplacer(EVENTGROUPS, { domain }));
        },
      },
      {
        index: 3,
        title: t('checklist.partnerPortalMigrated.item3.title'),
        description: t('checklist.partnerPortalMigrated.item3.description'),
        deactivated: !partnerPortalChecklist?.studioDataEdited,
        active: partnerPortalActiveIndex === PartnerPortalChecklistStep.STUDIO_DATA_EDITED,
        onClick: () => {
          if (!partnerPortalChecklist?.studioDataEdited) {
            updatePartnerPortalChecklist({ studioDataEdited: new Date().toISOString() });
            Segment.track(SegmentName.PARTNER_PORTAL_CHECKLIST_STUDIO_DATA_EDITED);
          }
          navigate(locationLink);
        },
      },
      {
        index: 4,
        title: t('checklist.partnerPortalMigrated.item4.title'),
        description: t('checklist.partnerPortalMigrated.item4.description'),
        deactivated: !partnerPortalChecklist?.staffEdited,
        active: partnerPortalActiveIndex === PartnerPortalChecklistStep.STAFF_EDITED,
        onClick: () => {
          if (!partnerPortalChecklist?.staffEdited) {
            updatePartnerPortalChecklist({ staffEdited: new Date().toISOString() });
            Segment.track(SegmentName.PARTNER_PORTAL_CHECKLIST_STAFF_EDITED);
          }
          navigate(routeReplacer(PROVIDER_STAFF, { domain }));
        },
      },
    ],
    [partnerPortalActiveIndex, partnerPortalChecklist, domain, locationLink, t],
  );

  useEffect(() => {
    if (checklistError) {
      Segment.track(SegmentName.CHECKLIST_ERRORED);
    }
  }, [checklistError]);

  useEffect(() => {
    if (dataUpdatedAt) {
      Segment.track(SegmentName.CHECKLIST_CALLED);
    }
  }, [dataUpdatedAt]);

  return {
    partnerPortalActiveIndex,
    partnerPortalChecklistItems,
    updatePartnerPortalChecklist,
    isCheckListLoading,
    checklistError,
    partnerPortalChecklist,
  };
};
